import { Alert, Box, Stack, Typography } from "@mui/material";
import { useSelector } from "@tanstack/react-store";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { withForm } from "@/components/Forms";
import CustomCard from "../../CreateDeliveryDeal/ui/custom-card";
import { deliveryOptionFormOpts } from "../option.form.opts";

const DYNAMIC_PRICE_MULTIPLIER = 0.8;
const FORMULA_EXAMPLE_DRIVER_AMOUNT = 50;

export const BasicInfoSection = withForm({
  ...deliveryOptionFormOpts,
  render: ({ form }) => {
    const { t } = useTranslation();
    const prevNameRef = useRef(form.getFieldValue("name") ?? "");
    const dynamicPricing = useSelector(form.store, (state) => state.values.dynamicPricing);
    const defaultClientPrice = useSelector(form.store, (state) => state.values.defaultClientPrice);

    // Sync partner assignment display names when the name changes
    const syncDisplayName = (value: string) => {
      const previousName = prevNameRef.current;
      prevNameRef.current = value;
      const assignments = form.getFieldValue("partnerAssignments");
      if (!Array.isArray(assignments)) return;
      assignments.forEach((a: any, i: number) => {
        if (!a.displayName || a.displayName === previousName) {
          form.setFieldValue(`partnerAssignments[${i}].displayName`, value);
        }
      });
    };

    const nameFieldListeners = {
      onChange: ({ value }: any) => syncDisplayName(value),
    };

    const exampleClientPrice =
      Number(defaultClientPrice ?? 0) + DYNAMIC_PRICE_MULTIPLIER * FORMULA_EXAMPLE_DRIVER_AMOUNT;

    return (
      <CustomCard
        label={t("deliveryOption.basicInfo.title")}
        subtitle={t("deliveryOption.basicInfo.subtitle")}
      >
        <Stack spacing={2}>
          <Box sx={{ minHeight: 70 }}>
            <form.AppField name="name" listeners={nameFieldListeners}>
              {(f) => <f.TextField label={t("deliveryOption.basicInfo.name")} />}
            </form.AppField>
          </Box>
          <form.AppField name="defaultClientPrice">
            {(f) => (
              <f.PriceField
                legend={true}
                min={0}
                label={
                  dynamicPricing
                    ? t("deliveryOption.basicInfo.basePrice")
                    : t("deliveryOption.basicInfo.price")
                }
              />
            )}
          </form.AppField>
          {dynamicPricing && (
            <Alert severity="info" variant="outlined">
              <Typography variant="body2" fontWeight={600} gutterBottom>
                {t("deliveryOption.basicInfo.formulaTitle")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t("deliveryOption.basicInfo.formulaHint")}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                {t("deliveryOption.basicInfo.formulaExample", {
                  base: Number(defaultClientPrice ?? 0).toFixed(2),
                  driver: FORMULA_EXAMPLE_DRIVER_AMOUNT.toFixed(2),
                  result: exampleClientPrice.toFixed(2),
                })}
              </Typography>
            </Alert>
          )}
        </Stack>
      </CustomCard>
    );
  },
});
